// upload-photos.component.ts (VERSÃO REFATORADA)

import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TreatmentPlanService } from 'src/app/services/treatment-plan.service';
import { QrLinkService } from 'src/app/services/qr-link.service';
import { unwrapError } from 'src/app/utils/log-error';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { ContextService } from 'src/app/services/context.service';
import { ITreatmentPlanProduct } from 'src/app/models/treatment-plan.model';
import { PhotoCaptureResult, PhotoCaptureService } from '../photo-capture.service';

@Component({
  selector: 'app-upload-photos',
  templateUrl: './upload-photos.component.html',
  styleUrls: ['./upload-photos.component.sass']
})
export class UploadPhotosComponent implements OnInit, OnDestroy {
  // Parâmetros da URL
  treatmentPlanId: string = '';
  productId: string = '';
  token: string = '';

  // Controle de UI
  loading: boolean = true; // Inicia como true até carregar os dados
  uploadComplete: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';

  // Controle para popup de seleção
  showCaptureOptions: boolean = false;
  currentPhotoIndex: number = -1;
  hasCamera: boolean = false;

  // Flag para indicar processamento HEIC
  processingHeic: boolean = false;

  // PROPRIEDADES PARA O MODAL DE PROGRESSO DE UPLOAD
  showUploadProgress: boolean = false;
  uploadProgress: number = 0;
  totalFilesToUpload: number = 0;
  filesUploaded: number = 0;
  currentUploadingFileName: string = '';

  // Fotos
  photos: {
    id: string | null;
    file: File | null;
    preview: SafeUrl | null;
    type: 'bottle' | 'box' | 'label';
    label: string;
    order: number;
    exists: boolean;
  }[] = [];

  /**
   * Subject de destruição: emitido em ngOnDestroy para cancelar qualquer subscription
   * via `takeUntil(this.destroy$)`. Corrige leak de route.queryParams em entrada/saída
   * repetida da tela.
   */
  private readonly destroy$ = new Subject<void>();

  /**
   * Política de retry para chamadas HTTP em redes móveis (QR Code flow é mobile).
   * Backoff exponencial — total máximo ~7s antes de desistir.
   */
  private static readonly MAX_RETRIES = 3;
  private static readonly BASE_RETRY_DELAY_MS = 1000;

  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private treatmentPlanService: TreatmentPlanService,
    private cdr: ChangeDetectorRef,
    private photoCaptureService: PhotoCaptureService,
    private contextService: ContextService,
    private qrLinkService: QrLinkService
  ) { }

  async ngOnInit(): Promise<void> {
    // ORDEM: detectar capacidades do dispositivo ANTES de reagir aos queryParams.
    // Antes, a detecção era async e concorria com loadProductAndPhotos — o botão
    // "usar câmera" podia piscar desabilitado brevemente. Agora garantimos o
    // estado de `hasCamera` antes da primeira render.
    try {
      const capabilities = await this.photoCaptureService.detectDeviceCapabilities();
      this.hasCamera = capabilities.hasCamera;
    } catch (err) {
      // Falha silenciosa: assume sem câmera. Usuário pode usar galeria.
      this.hasCamera = false;
    }

    // Tombamento total: URL pública agora carrega APENAS um token curto (?qr=...).
    // O backend resolve o token, retorna params + HMAC token de 30min. Esquema
    // antigo (?token=<JWT>&treatmentPlanId=...) não é mais suportado — QRs
    // antigos expiram em 1h e somem.
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(async (params) => {
      const qrToken = String(params['qr'] || '').trim();

      if (!qrToken) {
        this.errorMessage = 'Link inválido ou incompleto. Solicite um novo QR code à clínica.';
        this.loading = false;
        return;
      }

      try {
        const resolved = await firstValueFrom(this.qrLinkService.resolveProductPhotos(qrToken));

        // Aplica params do backend
        const p = resolved.params || {};
        this.treatmentPlanId = String(p['treatmentPlanId'] || '').trim();
        this.productId = String(p['productId'] || '').trim();

        // photoType usado pra UI (não no request)
        const photoType = String(p['photoType'] || '').trim();
        if (photoType) {
          // mantém pra compatibilidade futura — UI não usa hoje
        }

        // Modules / moduleId
        const modulesParam = p['modules'];
        const moduleId = p['moduleId'];
        try {
          if (modulesParam) {
            const decoded = JSON.parse(atob(String(modulesParam)));
            if (Array.isArray(decoded)) {
              this.authService.setModules(decoded);
            }
          }
          if (moduleId) {
            const modules = this.authService.getModules();
            const sel = modules.find((m: any) => m.id === moduleId);
            if (sel) {
              this.authService.setSelectedModule(sel);
            }
          }
        } catch (e) {
          console.warn('Falha ao aplicar módulos do deep link');
        }

        // Clínica ativa (vinda do backend)
        const clinicId = String(p['clinicId'] || '').trim();
        if (clinicId) {
          this.contextService.setActiveClinic({ id: clinicId, cnpj: clinicId, name: '' });
        }

        // HMAC token de 30min retornado pelo backend — autentica os requests
        // subsequentes (GET plano, PUT photos) via fluxo NOAR-APP-TOKEN.
        this.token = resolved.hmacToken;
        this.authService.setToken(this.token);

        if (!this.treatmentPlanId || !this.productId) {
          this.errorMessage = 'QR com parâmetros inválidos. Solicite um novo QR code à clínica.';
          this.loading = false;
          return;
        }

        console.info('[UPLOAD PHOTOS] QR resolvido com sucesso', {
          qrToken,
          treatmentPlanId: this.treatmentPlanId,
          productId: this.productId,
          hasClinicId: !!clinicId,
          hmacExpiresAt: resolved.expiresAt,
        });

        this.loadProductAndPhotos();

      } catch (err: unknown) {
        const details = unwrapError(err);
        // 404 = token não existe; 400 com serverMessage = expirado; 0 = rede.
        console.error('[UPLOAD PHOTOS] Falha ao resolver QR', {
          qrToken,
          ...details,
        });
        if (details.status === 404) {
          this.errorMessage = 'QR não encontrado. Solicite um novo link à clínica.';
        } else if (details.status === 400 && details.serverMessage) {
          this.errorMessage = details.serverMessage;
        } else if (details.status === 0 || details.name === 'TimeoutError') {
          this.errorMessage = 'Sem conexão. Verifique sua internet e tente abrir o link de novo.';
        } else {
          this.errorMessage = details.serverMessage || 'Não foi possível abrir o link. Tente novamente.';
        }
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    // Emite destroy para todos os takeUntil subscritos — cancela route.queryParams
    // e qualquer HTTP observable pendente.
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProductAndPhotos(): void {
    this.treatmentPlanService.getTreatmentPlanById(this.treatmentPlanId).then(plan => {
      const product = plan.products.find(p => String(p.id) === this.productId);
      if (!product) {
        this.errorMessage = 'Produto não encontrado no plano de tratamento.';
        this.loading = false;
        return;
      }
      this.processProductPhotos(product);
    }).catch((err: unknown) => {
      const details = unwrapError(err);
      console.error('[UPLOAD PHOTOS] Falha ao carregar plano', {
        planId: this.treatmentPlanId,
        productId: this.productId,
        ...details,
      });
      this.errorMessage = details.serverMessage || 'Erro ao carregar dados do tratamento.';
      this.loading = false;
    });
  }

  processProductPhotos(product: ITreatmentPlanProduct): void {
    const photoMap: { [key: string]: { url: string, id: string } } = {};
    if (product.photos) {
      product.photos.forEach(photo => {
        const type = this.getPhotoTypeFromOrder(photo.photo_order);
        if (type && photo.photo) {
          if(this.photoCaptureService.isUrl(photo.photo)) {
            photoMap[type] = { url: photo.photo, id: photo.id! };
          }
          else {
            photoMap[type] = { url: `data:image/jpeg;base64,${photo.photo}`, id: photo.id! };
          }
        }
      });
    }

    this.photos = [
      { id: photoMap['bottle']?.id || null, preview: photoMap['bottle'] ? this.sanitizer.bypassSecurityTrustUrl(photoMap['bottle'].url) : null, exists: !!photoMap['bottle'], type: 'bottle', label: 'Frasco', order: 0, file: null },
      { id: photoMap['box']?.id || null, preview: photoMap['box'] ? this.sanitizer.bypassSecurityTrustUrl(photoMap['box'].url) : null, exists: !!photoMap['box'], type: 'box', label: 'Caixa', order: 1, file: null },
      { id: photoMap['label']?.id || null, preview: photoMap['label'] ? this.sanitizer.bypassSecurityTrustUrl(photoMap['label'].url) : null, exists: !!photoMap['label'], type: 'label', label: 'Etiqueta', order: 2, file: null }
    ];
    this.loading = false;
  }

  // Abre o modal de opções de captura
  selectPhoto(photoIndex: number): void {
    // Race guard: se um processamento anterior (HEIC/upload) ainda está em andamento,
    // bloqueia a troca de slot. Antes, o usuário podia clicar em outro slot enquanto
    // o upload anterior processava, fazendo o resultado cair no slot errado.
    if (this.processingHeic || this.showUploadProgress) {
      this.errorMessage = 'Aguarde o processamento anterior terminar.';
      setTimeout(() => this.errorMessage = '', 2000);
      return;
    }

    if (this.photos[photoIndex].exists) {
      // Não permite alterar fotos que já existem no servidor a partir desta tela
      return;
    }
    this.currentPhotoIndex = photoIndex;
    this.showCaptureOptions = true;
  }

  cancelCaptureOptions(): void {
    this.showCaptureOptions = false;
    this.currentPhotoIndex = -1;
  }

  // MÉTODO NOVO: Chama o serviço para usar a câmera
  async captureWithCamera(): Promise<void> {
    this.showCaptureOptions = false;
    if (this.currentPhotoIndex === -1) return;

    // Snapshot do índice para proteger contra mudança de slot durante o await
    const capturedIndex = this.currentPhotoIndex;

    try {
      this.processingHeic = true;
      const result = await this.photoCaptureService.openNativeCamera();
      this.processPhotoResult(result, capturedIndex);
    } catch (error) {
      console.warn('Captura de foto cancelada');
    } finally {
      this.processingHeic = false;
    }
  }

  // MÉTODO NOVO: Chama o serviço para usar a galeria (seleção única)
  async selectFromGallery(): Promise<void> {
    this.showCaptureOptions = false;
    if (this.currentPhotoIndex === -1) return;

    const capturedIndex = this.currentPhotoIndex;

    try {
      this.processingHeic = true;
      // Usamos o seletor de arquivo ÚNICO aqui
      const result = await this.photoCaptureService.openFileSelector();
      this.processPhotoResult(result, capturedIndex);
    } catch (error) {
      console.warn('Seleção da galeria cancelada');
    } finally {
      this.processingHeic = false;
    }
  }

  // MÉTODO NOVO: Centraliza o processamento do resultado do serviço.
  // Recebe `photoIndex` como parâmetro (snapshot tirado ANTES do await) — isso
  // fecha a race condition: se o usuário clicou em outro slot enquanto o serviço
  // processava, o resultado ainda vai para o slot correto originalmente selecionado.
  private processPhotoResult(result: PhotoCaptureResult, photoIndex: number): void {
    if (photoIndex >= 0 && photoIndex < this.photos.length) {
      this.photos[photoIndex].file = result.file;
      this.photos[photoIndex].preview = result.preview;

      this.successMessage = `Foto "${this.photos[photoIndex].label}" adicionada.`;
      setTimeout(() => this.successMessage = '', 3000);
    }
    this.currentPhotoIndex = -1;
  }

  // Lógica de remoção local
  removePhoto(photoToRemove: any): void {
    const photoIndex = this.photos.findIndex(p => p === photoToRemove);
    if (photoIndex > -1) {
      this.photos[photoIndex].file = null;
      this.photos[photoIndex].preview = null;
    }
  }

  async uploadPhotos(): Promise<void> {
    const photosToUpload = this.photos.filter(p => p.file);
    if (photosToUpload.length === 0) {
      this.errorMessage = 'Nenhuma nova foto selecionada para enviar.';
      return;
    }

    // 1. INICIALIZA O ESTADO DO PROGRESSO
    this.totalFilesToUpload = photosToUpload.length;
    this.filesUploaded = 0;
    this.uploadProgress = 0;
    this.showUploadProgress = true; // Mostra o modal de progresso
    this.errorMessage = '';

    // try/catch externo garante que o modal NUNCA fique preso em "progresso"
    // se qualquer erro não previsto escapar (rede, exception em código de UI, etc.)
    try {
      // 2. FAZ O UPLOAD DE CADA FOTO, UMA POR UMA (SEQUENCIALMENTE)
      for (const photo of photosToUpload) {
        try {
          this.currentUploadingFileName = photo.file!.name;

          const fileRequest = { file_name: photo.file!.name, content_type: photo.file!.type };

          // Retry em rede mobile: generatePresignedUrls falha em 4G instável.
          const presignedData = await this.withRetry(
            () => this.treatmentPlanService.generatePresignedUrls(
              this.treatmentPlanId, [fileRequest], this.productId
            ),
            'generatePresignedUrls'
          );
          const uploadData = presignedData[0];

          // Upload pro S3 também com retry — em redes lentas pode dar timeout.
          await this.withRetry(
            () => this.treatmentPlanService.uploadFileToS3(uploadData.uploadUrl, photo.file!, photo.file!.type),
            'uploadFileToS3'
          );

          const photoForDb = { photo: uploadData.objectKey, photo_order: photo.order };
          await this.withRetry(
            () => this.treatmentPlanService.uploadProductPhotos(this.treatmentPlanId, this.productId, [photoForDb]),
            'uploadProductPhotos'
          );

          // 3. ATUALIZA O PROGRESSO APÓS SUCESSO
          this.filesUploaded++;
          this.uploadProgress = (this.filesUploaded / this.totalFilesToUpload) * 100;

          // Limpa o arquivo para não ser reenviado
          photo.file = null;

        } catch (error) {
          console.error(`Falha no upload da foto ${photo.label}`);
          this.errorMessage = `Ocorreu um erro ao enviar "${photo.file?.name}". O processo foi interrompido.`;
          // Re-throw para ser capturado pelo catch externo — garante cleanup do modal
          throw error;
        }
      }

      // 4. FINALIZA O PROCESSO
      this.successMessage = 'Fotos enviadas com sucesso!';
      this.uploadComplete = true;

      // Esconde o modal de progresso após um pequeno delay
      setTimeout(() => {
        this.showUploadProgress = false;
        this.loadProductAndPhotos(); // Recarrega as fotos para mostrar as novas
      }, 1500);
    } catch (error) {
      // Garante que o modal de progresso fecha em qualquer erro não previsto
      this.showUploadProgress = false;
      if (!this.errorMessage) {
        this.errorMessage = 'Erro desconhecido durante upload. Tente novamente.';
      }
    }
  }

  /**
   * Executa uma operação assíncrona com retry + backoff exponencial.
   *
   * Ordem do delay: 1s, 2s, 4s (total ~7s antes de desistir na 3ª tentativa).
   * Justificativa: mobile 4G intermitente precisa de retry; mas esperar mais
   * que ~10s piora UX pior que falhar rápido e pedir para tentar de novo.
   */
  private async withRetry<T>(op: () => Promise<T>, label: string): Promise<T> {
    let lastError: unknown;
    for (let attempt = 0; attempt < UploadPhotosComponent.MAX_RETRIES; attempt++) {
      try {
        return await op();
      } catch (err) {
        lastError = err;
        if (attempt < UploadPhotosComponent.MAX_RETRIES - 1) {
          const delay = UploadPhotosComponent.BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
          console.warn(`[UPLOAD] ${label} falhou (tentativa ${attempt + 1}/${UploadPhotosComponent.MAX_RETRIES}), tentando novamente em ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    throw lastError;
  }


  finish(): void {
    alert('Você pode fechar esta página agora. As fotos foram enviadas com sucesso!');
  }

  // Funções auxiliares para o template
  hasSelectedPhotos(): boolean { return this.photos.some(p => p.file !== null); }
  allPhotosSelected(): boolean { return this.photos.every(p => p.file !== null || p.exists); }

  private getPhotoTypeFromOrder(order: number): string | null {
    switch (order) {
      case 0:
        return 'bottle';
      case 1:
        return 'box';
      case 2:
        return 'label';
      default:
        return null;
    }
  }
}
